import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactEventHandler,
  type ReactNode,
} from "react";
import Hls, { Level } from "hls.js";

import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlaybackRateButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaMuteButton,
  MediaFullscreenButton,
  MediaPosterImage,
} from "media-chrome/react";
import QualitySettings from "./QualitySettings";
import { useMediaQuery } from "@mui/material";

type PlayerProps = {
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  className?: string;
  poster?: string;
  style?: CSSProperties;
  videoObjectFit?: CSSProperties["objectFit"];
  videoObjectPosition?: CSSProperties["objectPosition"];
  onLoadStart?: ReactEventHandler<HTMLVideoElement>;
  onCanPlay?: ReactEventHandler<HTMLVideoElement>;
  onWaiting?: ReactEventHandler<HTMLVideoElement>;
  onPlaying?: ReactEventHandler<HTMLVideoElement>;
  onPlay?: ReactEventHandler<HTMLVideoElement>;
  onPause?: ReactEventHandler<HTMLVideoElement>;
  onEnded?: ReactEventHandler<HTMLVideoElement>;
  onLoadedData?: ReactEventHandler<HTMLVideoElement>;
  enableMobileTapPlay?: boolean;
  enableDesktopTapPlay?: boolean;
  showControls?: boolean;
  children?: ReactNode;
  credentials?: "true" | "false" | "none"; // Optional prop to control the credentials attribute; "none" omits crossOrigin entirely
};

const VIDEO_QUALITY_STORAGE_KEY = "videoQuality";
const DEFAULT_QUALITY_HEIGHT = 720;

const resolveDefaultLevelIndex = (levels: Level[]): number => {
  if (!levels.length) return -1;

  const exactIndex = levels.findIndex(
    (level) => level.height === DEFAULT_QUALITY_HEIGHT
  );
  if (exactIndex !== -1) return exactIndex;

  const atOrBelowDefault = levels
    .map((level, index) => ({ index, height: level.height }))
    .filter((entry) => Number.isFinite(entry.height) && entry.height <= DEFAULT_QUALITY_HEIGHT)
    .sort((a, b) => b.height - a.height);

  if (atOrBelowDefault.length > 0) {
    return atOrBelowDefault[0].index;
  }

  const aboveDefault = levels
    .map((level, index) => ({ index, height: level.height }))
    .filter((entry) => Number.isFinite(entry.height) && entry.height > DEFAULT_QUALITY_HEIGHT)
    .sort((a, b) => a.height - b.height);

  return aboveDefault.length > 0 ? aboveDefault[0].index : -1;
};

const Player = forwardRef<HTMLVideoElement, PlayerProps>(function Player(
  {
    src,
    autoPlay = true,
    muted = false,
    className,
    style,
    videoObjectFit = "cover",
    videoObjectPosition = "center",
    onLoadStart,
    onCanPlay,
    onWaiting,
    onPlaying,
    onPlay,
    onPause,
    onEnded,
    onLoadedData,
    poster,
    enableMobileTapPlay = true,
    enableDesktopTapPlay = false,
    showControls = true,
    children,
    credentials
  },
  ref
) {
  const internalVideoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const lastPointerUpAtRef = useRef(0);
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [levels, setLevels] = useState<Level[]>([]);
  const [currentLevel, setCurrentLevel] = useState(-1);

  const setVideoRef = useCallback(
    (node: HTMLVideoElement | null) => {
      internalVideoRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video || !src) return;
    const isHlsSource = /\.m3u8(\?.*)?$/i.test(src);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let autoplayAttempts = 0;
    const MAX_AUTOPLAY_ATTEMPTS = 10;

    const attemptAutoplay = () => {
      if (!autoPlay || !video.paused || autoplayAttempts >= MAX_AUTOPLAY_ATTEMPTS) return;
      autoplayAttempts += 1;
      video.play().catch((err: unknown) => {
        // On slow networks the element may not have enough buffered data yet
        // for play() to succeed on the first readiness event; keep retrying
        // until playback actually starts or we give up after MAX_AUTOPLAY_ATTEMPTS.
        console.warn(`Autoplay attempt ${autoplayAttempts} failed, retrying:`, err);
        retryTimeoutId = setTimeout(attemptAutoplay, 500);
      });
    };

    const retryEvents: Array<keyof HTMLMediaElementEventMap> = [
      "loadedmetadata",
      "loadeddata",
      "canplay",
      "canplaythrough",
    ];
    retryEvents.forEach((evt) => video.addEventListener(evt, attemptAutoplay));

    if (isHlsSource && Hls.isSupported() && video) {
      const hls = new Hls({
        xhrSetup: (xhr) => { xhr.withCredentials = credentials === "true"; }, // send CloudFront cookies
      });
      setLevels([]);
      setCurrentLevel(-1);

      hls.loadSource(src);
      hls.attachMedia(video);
      attemptAutoplay();

      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        setLevels(data.levels);

        const savedQuality = localStorage.getItem(VIDEO_QUALITY_STORAGE_KEY);
        const useDefaultQuality = !savedQuality || savedQuality === "auto";

        if (useDefaultQuality) {
          const defaultIndex = resolveDefaultLevelIndex(data.levels);
          if (defaultIndex === -1) {
            hls.currentLevel = -1;
            setCurrentLevel(-1);
            return;
          }

          hls.currentLevel = defaultIndex;
          setCurrentLevel(defaultIndex);
          localStorage.setItem(
            VIDEO_QUALITY_STORAGE_KEY,
            String(DEFAULT_QUALITY_HEIGHT)
          );
          return;
        }

        const targetHeight = Number(savedQuality);
        if (!Number.isFinite(targetHeight)) {
          const defaultIndex = resolveDefaultLevelIndex(data.levels);
          if (defaultIndex === -1) {
            hls.currentLevel = -1;
            setCurrentLevel(-1);
            return;
          }

          hls.currentLevel = defaultIndex;
          setCurrentLevel(defaultIndex);
          localStorage.setItem(
            VIDEO_QUALITY_STORAGE_KEY,
            String(DEFAULT_QUALITY_HEIGHT)
          );
          return;
        }

        const exactIndex = data.levels.findIndex(
          (level) => level.height === targetHeight
        );
        const selectedIndex =
          exactIndex !== -1
            ? exactIndex
            : data.levels.reduce((closestIndex, level, index) => {
              const closestDiff = Math.abs(
                data.levels[closestIndex].height - targetHeight
              );
              const currentDiff = Math.abs(level.height - targetHeight);
              return currentDiff < closestDiff ? index : closestIndex;
            }, 0);

        hls.currentLevel = selectedIndex;
        setCurrentLevel(selectedIndex);
        attemptAutoplay();
      });

      hlsRef.current = hls;
    } else if (
      isHlsSource &&
      video &&
      video.canPlayType("application/vnd.apple.mpegurl")
    ) {
      video.src = src;
      video.load();
      attemptAutoplay();
    } else {
      // Native playback fallback for non-HLS URLs like MP4.
      setLevels([]);
      setCurrentLevel(-1);
      video.src = src;
      video.load();
      attemptAutoplay();
    }

    return () => {
      retryEvents.forEach((evt) => video.removeEventListener(evt, attemptAutoplay));
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, [src, autoPlay]);

  const changeQuality = (level: number) => {
    if (hlsRef.current) {
      hlsRef.current.currentLevel = level;
      setCurrentLevel(level);

      if (level === -1) {
        localStorage.setItem(VIDEO_QUALITY_STORAGE_KEY, "auto");
        return;
      }

      const selectedHeight = hlsRef.current.levels[level]?.height;
      if (selectedHeight) {
        localStorage.setItem(
          VIDEO_QUALITY_STORAGE_KEY,
          String(selectedHeight)
        );
      }
    }
  };

  const toggleSurfacePlayback = useCallback(() => {
    const videoElement = internalVideoRef.current;
    if (!videoElement) return;

    if (!videoElement.paused) {
      videoElement.pause();
      return;
    }

    videoElement.play().catch((err: unknown) => {
      console.error("Error playing video on surface tap:", err);
    });
  }, []);

  const canToggleOnSurface = isMobile
    ? enableMobileTapPlay
    : enableDesktopTapPlay;

  return (
    <MediaController
      className={className}
      style={{
        width: "100%",
        minWidth: 0,
        position: "relative",
        backgroundColor: "black",
        // Keep poster and media rendering consistent across Media Chrome + native video.
        ...({
          "--media-object-fit": videoObjectFit,
          "--media-object-position": videoObjectPosition,
        } as any),
        ...style,
      }}
    >
      <video
        ref={setVideoRef}
        slot="media"
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        crossOrigin={credentials === "true" ? "use-credentials" : credentials === "none" ? undefined : "anonymous"} // Set crossOrigin based on the credentials prop; "none" skips it for third-party URLs whose redirect chains don't carry CORS headers
        onPointerUp={(event) => {
          if (!canToggleOnSurface) return;
          lastPointerUpAtRef.current = event.timeStamp;
          toggleSurfacePlayback();
        }}
        onClick={(event) => {
          if (!canToggleOnSurface) return;
          // Most browsers emit click after pointerup for the same interaction.
          if (event.timeStamp - lastPointerUpAtRef.current < 350) return;
          toggleSurfacePlayback();
        }}
        onLoadStart={onLoadStart}
        onCanPlay={onCanPlay}
        onWaiting={onWaiting}
        onPlaying={onPlaying}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        onLoadedData={onLoadedData}
        poster={poster}
        style={{
          width: "100%",
          height: "100%",
          objectFit: videoObjectFit,
          objectPosition: videoObjectPosition,
        }}
      />

      {poster && (
        <MediaPosterImage slot="poster" src={poster} style={{ width: "100%", height: "100%", objectFit: videoObjectFit, objectPosition: videoObjectPosition }} />
      )}

      {children}

      {showControls && (
        <MediaControlBar>
          <MediaPlayButton />
          {!isMobile && <MediaSeekBackwardButton seekOffset={10} />}
          {!isMobile && <MediaSeekForwardButton seekOffset={10} />}

          <MediaTimeRange />
          <MediaTimeDisplay showDuration />

          <MediaMuteButton />
          <MediaVolumeRange />
          <MediaPlaybackRateButton />
          {/* {!isMobile && <MediaCaptionsButton />} */}
          <QualitySettings
            levels={levels}
            currentLevel={currentLevel}
            changeQuality={changeQuality}
          />
          <MediaFullscreenButton />
        </MediaControlBar>
      )}
    </MediaController>
  );
});

export default Player;
